import React, { useState, useEffect, useMemo, useCallback } from "react";
import Button from "../components/ui/Button";
import { useAuth } from "../hooks/AuthProvider";

const ModalInfo = ({ isOpen, onClose, offerId, modalType, supplierEmail }) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { user } = useAuth();

  const [data, setData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  // Usar useMemo para evitar recalcular en cada render
  const isFormValid = useMemo(() => {
    const nameValid = data.name !== "";
    const emailValid = data.email !== "";
    const messageValid = data.message !== "";
    const isValid = nameValid && emailValid && messageValid;

    // Log de validación usando setTimeout para evitar problemas con ResizeObserver
    setTimeout(() => {
      console.log("🔍 Validación del formulario:", {
        [`${nameValid ? "✓" : "✗"} name`]: data.name || "(vacío)",
        [`${emailValid ? "✓" : "✗"} email`]: data.email || "(vacío)",
        [`${messageValid ? "✓" : "✗"} message`]: data.message || "(vacío)",
        [`${isValid ? "✓" : "✗"} Formulario válido`]: isValid,
      });
    }, 0);
    return isValid;
  }, [data.name, data.email, data.message]);

  function validateInputs(inputs) {
    let errors = {};
    if (!inputs.name.trim()) {
      errors.name = "El nombre es obligatorio.";
    }
    if (!inputs.email) {
      errors.email = "El email es obligatorio.";
    } else if (!/\S+@\S+\.\S+/.test(inputs.email)) {
      errors.email = "Email no válido.";
    }
    if (!inputs.message.trim()) {
      errors.message = "El mensaje es obligatorio.";
    }
    return errors;
  }

  const [errors, setErrors] = useState({});
  const [isRequestSuccessful, setRequestSuccessful] = useState(false);

  useEffect(() => {
    if (isRequestSuccessful) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isRequestSuccessful, onClose]);

  // Resetear el formulario cuando se cierra el modal
  useEffect(() => {
    if (!isOpen) {
      setData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
      setErrors({});
      setRequestSuccessful(false);
    }
  }, [isOpen]);

  // Usar requestAnimationFrame para evitar problemas con ResizeObserver
  useEffect(() => {
    if (!isOpen) return;

    const timeoutId = setTimeout(() => {
      const nameValid = data.name !== "";
      const emailValid = data.email !== "";
      const messageValid = data.message !== "";

      console.log("📝 Estado actual del formulario:", {
        [`${nameValid ? "✓" : "✗"} Nombre`]: data.name || "(vacío)",
        [`${emailValid ? "✓" : "✗"} Email`]: data.email || "(vacío)",
        [`${messageValid ? "✓" : "✗"} Mensaje`]: data.message || "(vacío)",
        [`${isFormValid ? "✓" : "✗"} Botón habilitado`]: isFormValid,
      });
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [data, isFormValid, isOpen]);

  async function handleSendQuestion(event) {
    event.preventDefault();
    const validationErrors = validateInputs(data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      try {
        // Cambiar product_question a cta y añadir título
        const notificationType = modalType === "product_question" ? "cta" : modalType;

        const payload = {
          name: data.name,
          email: data.email,
          phone: data.phone,
          message: data.message,
          type: notificationType,
          model_id: offerId.toString(),
        };

        // Si el tipo es cta (product_question convertido), añadir título
        if (notificationType === "cta") {
          payload.title = "Formulario pedir presupuesto web";
        }

        // Si el tipo no es "expert", añadir el campo "to" (obligatorio según la API)
        if (modalType !== "expert") {
          // En el to solo puede haber un correo (el del proveedor)
          if (supplierEmail) {
            payload.to = supplierEmail;
          }
          // CC como array con pedidos@elalquilador.com y el email del usuario
          payload.cc = data.email;
        }

        const response = await fetch(`${API_BASE_URL}/send-notification`, {
          method: "POST",
          body: JSON.stringify(payload),
          headers: {
            "Content-type": "application/json; charset=UTF-8",
          },
        });
        const responseData = await response.json();
        console.log(JSON.stringify(responseData));
        if (response.ok) {
          setErrors({});
          setRequestSuccessful(true);
        } else {
          throw new Error(responseData.message || "Ha habido un error, intentálo otra vez.");
        }
      } catch (error) {
        setErrors({ error });
      }
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl w-full p-8 max-w-2xl">
        {isRequestSuccessful ? (
          <div className="text-center fade-in flex flex-col gap-3">
            <h2 className="text-3xl text-primary font-bold">¡Gracias!</h2>
            <p>Se ha enviado tu correo al proveedor correctamente. En breve se pondrán en contacto contigo</p>
            <p>También hemos enviado una copia a tu correo.</p>
          </div>
        ) : (
          <div className="contents">
            <h2 className="text-xl font-bold mb-8">Más información sobre el producto</h2>
            <form className="flex flex-col gap-4" onSubmit={handleSendQuestion}>
              <div className="mb-4 flex flex-col">
                <label className="text-left pl-2 text-primary font-bold" htmlFor="name">
                  Nombre *
                </label>
                <input
                  id="name"
                  type="text"
                  value={user?.name ? user.name : data.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Usar setTimeout para evitar problemas con ResizeObserver
                    setTimeout(() => {
                      console.log("✏️ Campo 'name' cambiado:", value);
                    }, 0);
                    setData((prev) => ({ ...prev, name: value }));
                  }}
                  placeholder="introduzca su nombre"
                  className="focus:outline-none px-4 border rounded-full h-10 border-primary"
                />
                {errors.name && <p className="text-xs mt-1 text-red-500 text-right w-full">{errors.name}</p>}
              </div>
              <div className="mb-4 flex flex-col">
                <label className="text-left pl-2 text-primary font-bold" htmlFor="email">
                  Correo *
                </label>
                <input
                  id="email"
                  type="text"
                  value={user?.email ? user.email : data.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Usar setTimeout para evitar problemas con ResizeObserver
                    setTimeout(() => {
                      console.log("✏️ Campo 'email' cambiado:", value);
                    }, 0);
                    setData((prev) => ({ ...prev, email: value }));
                  }}
                  placeholder="introduzca su correo electrónico"
                  className="focus:outline-none px-4 border rounded-full h-10 border-primary"
                />
                {errors.email && <p className="text-xs mt-1 text-red-500 text-right w-full">{errors.email}</p>}
              </div>
              <div className="mb-4 flex flex-col">
                <label className="text-left pl-2 text-primary font-bold" htmlFor="phone">
                  Teléfono
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={data.phone}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Usar setTimeout para evitar problemas con ResizeObserver
                    setTimeout(() => {
                      console.log("✏️ Campo 'phone' cambiado:", value);
                    }, 0);
                    setData((prev) => ({ ...prev, phone: value }));
                  }}
                  placeholder="introduzca su teléfono (opcional)"
                  className="focus:outline-none px-4 border rounded-full h-10 border-primary"
                />
                {errors.phone && <p className="text-xs mt-1 text-red-500 text-right w-full">{errors.phone}</p>}
              </div>
              <div className="mb-4 flex flex-col">
                <label className="text-left pl-2 text-primary font-bold" htmlFor="message">
                  Mensaje *
                </label>
                <textarea
                  id="message"
                  value={data.message}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Usar setTimeout para evitar problemas con ResizeObserver
                    setTimeout(() => {
                      console.log("✏️ Campo 'message' cambiado:", value);
                    }, 0);
                    setData((prev) => ({ ...prev, message: value }));
                  }}
                  placeholder="introduzca su consulta"
                  rows="4"
                  className="focus:outline-none resize-none px-4 py-2 border rounded-xl border-primary"
                />
                {errors.message && <p className="text-xs mt-1 text-red-500 text-right w-full">{errors.message}</p>}
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="primary" className="border-primary" onClick={onClose}>
                  Cancelar
                </Button>
                <Button
                  disabled={!isFormValid}
                  className="{py-2}"
                  type="primary"
                  onClick={() => {
                    setTimeout(() => {
                      const nameValid = data.name !== "";
                      const emailValid = data.email !== "";
                      const messageValid = data.message !== "";

                      console.log("🔘 Botón 'Enviar' clickeado. Estado del formulario:", {
                        [`${nameValid ? "✓" : "✗"} Nombre`]: data.name || "(vacío)",
                        [`${emailValid ? "✓" : "✗"} Email`]: data.email || "(vacío)",
                        [`${messageValid ? "✓" : "✗"} Mensaje`]: data.message || "(vacío)",
                        [`${isFormValid ? "✓" : "✗"} Formulario válido`]: isFormValid,
                      });
                    }, 0);
                  }}
                >
                  Enviar
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModalInfo;
